import React, { useEffect, useRef } from 'react';

/**
 * A dialog for the ticket page: a labelled modal that takes the focus, keeps
 * Tab inside itself, closes on Escape or a click outside, and hands the
 * focus back to whatever opened it. Its own rather than the to-do screens'
 * (components/todos/TodoPeople Dialog), which is being redesigned on its own
 * branch; the two are deliberately alike.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function TicketDialog({ label, onClose, children, wide = false, className = '' }) {
  const box = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const back = document.activeElement;
    const el = box.current;
    const first = el && el.querySelector('textarea:not([disabled]), input:not([disabled]):not([type="hidden"]):not([type="checkbox"]), select:not([disabled])');
    (first || el)?.focus({ preventScroll: true });
    const was = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close.current(); return; }
      if (e.key !== 'Tab' || !box.current) return;
      const els = [...box.current.querySelectorAll(FOCUSABLE)].filter((x) => x.offsetParent !== null);
      if (!els.length) return;
      const a = els[0];
      const z = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === a || document.activeElement === box.current)) {
        e.preventDefault(); z.focus();
      } else if (!e.shiftKey && document.activeElement === z) {
        e.preventDefault(); a.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = was;
      if (back && typeof back.focus === 'function' && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch { /* gone */ }
      }
    };
  }, []);

  return (
    <div className="tk-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={box} className={`tk-dialog ${wide ? 'tk-dialog-wide' : ''} ${className}`.trim()}
           role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}>
        <div className="tk-dialog-head">
          <h2 className="tk-dialog-title">{label}</h2>
          <button type="button" className="tk-icon-btn" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" fill="none"
                 stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        <div className="tk-dialog-body">{children}</div>
      </div>
    </div>
  );
}
